import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Modal, 
    TextField, 
    Button, 
    Grid, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    Select, 
    Autocomplete, 
    Chip, 
    CircularProgress, 
    Snackbar, 
    Alert, 
    IconButton,
    Card,
    CardMedia,
    Divider,
    Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import FileUploadDropzone from './FileUploadDropzone';
import { RoomService } from '../api/services/roomService';
import { useCurrency } from '../utils/currency';

export default function RoomTypeFormModal({ open, onClose, roomType = null, onRefresh }) {
    const { currencySymbol } = useCurrency();
    const [loading, setLoading] = useState(false);
    const [amenitiesList, setAmenitiesList] = useState([]);
    const [isLoadingSetup, setIsLoadingSetup] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        baseRate: '',
        extraBedCharge: '',
        maxAdults: '',
        maxKids: '',
        defaultAmenities: [],
        images: [],
        status: 'Active'
    });
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        const fetchAmenities = async () => {
            if (!open) return;
            setIsLoadingSetup(true);
            try {
                const amenitiesRes = await RoomService.getAmenities();
                setAmenitiesList(amenitiesRes.data || amenitiesRes || []);
            } catch (error) {
                console.error("Failed to fetch amenities:", error);
            } finally {
                setIsLoadingSetup(false);
            }
        };
        fetchAmenities();
    }, [open]);

    useEffect(() => {
        if (roomType) {
            const rawAmenities = roomType.defaultAmenities || roomType.amenities || [];
            const mappedAmenities = rawAmenities.map(a => typeof a === 'string' ? a : (a?.name || '')).filter(Boolean);
            const initialImages = Array.isArray(roomType.images) 
                ? roomType.images 
                : (roomType.image ? [roomType.image] : []);
            
            setFormData({
                name: roomType.name || '',
                description: roomType.description || '',
                baseRate: roomType.baseRate !== undefined && roomType.baseRate !== null ? roomType.baseRate : '',
                extraBedCharge: roomType.extraBedCharge !== undefined && roomType.extraBedCharge !== null ? roomType.extraBedCharge : (roomType.extraBedRate !== undefined && roomType.extraBedRate !== null ? roomType.extraBedRate : ''),
                maxAdults: roomType.maxAdults !== undefined && roomType.maxAdults !== null ? roomType.maxAdults : '',
                maxKids: roomType.maxKids !== undefined && roomType.maxKids !== null ? roomType.maxKids : '',
                defaultAmenities: mappedAmenities,
                images: initialImages,
                status: roomType.status || 'Active'
            });
        } else {
            setFormData({
                name: '',
                description: '',
                baseRate: '',
                extraBedCharge: '',
                maxAdults: '',
                maxKids: '',
                defaultAmenities: [],
                images: [],
                status: 'Active'
            });
        }
    }, [roomType, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAmenitiesChange = (event, newValue) => {
        setFormData(prev => ({ ...prev, defaultAmenities: newValue }));
    };

    const handleImagesUploaded = (urls) => {
        if (!urls || urls.length === 0) return;
        setFormData(prev => ({
            ...prev,
            images: [...(prev.images || []), ...urls]
        }));
    };

    const handleRemoveImage = (indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            images: (prev.images || []).filter((_, idx) => idx !== indexToRemove)
        }));
    };

    const handleSetCoverImage = (indexToCover) => {
        if (indexToCover === 0) return;
        setFormData(prev => {
            const newImages = [...(prev.images || [])];
            const [selected] = newImages.splice(indexToCover, 1);
            newImages.unshift(selected);
            return { ...prev, images: newImages };
        });
    };

    const handleClearAllImages = () => {
        setFormData(prev => ({ ...prev, images: [] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            const payload = {
                ...formData,
                baseRate: parseFloat(formData.baseRate) || 0,
                extraBedCharge: formData.extraBedCharge !== '' && formData.extraBedCharge !== null ? parseFloat(formData.extraBedCharge) : 0,
                maxAdults: parseInt(formData.maxAdults, 10) || 0,
                maxKids: parseInt(formData.maxKids, 10) || 0,
                images: Array.isArray(formData.images) ? formData.images : [],
                id: roomType?.id
            };

            await RoomService.saveRoomType(payload, tenantId);
            
            setNotification({ 
                open: true, 
                message: `Room Type ${roomType ? 'updated' : 'created'} successfully!`, 
                severity: 'success' 
            });
            
            if (onRefresh) onRefresh();
            setTimeout(onClose, 1000);
        } catch (error) {
            console.error('Failed to save room type:', error);
            if (error.response?.status === 403 || error.response?.status === 405) {
                setNotification({ 
                    open: true, 
                    message: 'Action not allowed while the hotel is suspended.', 
                    severity: 'error' 
                });
            } else {
                setNotification({ 
                    open: true, 
                    message: error.message || 'Failed to save room type', 
                    severity: 'error' 
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: { xs: '92%', sm: 650 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
                    maxHeight: '90vh', overflowY: 'auto'
                }}>
                    <IconButton sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} disabled={loading}>
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h5" fontWeight="bold" mb={3}>
                        {roomType ? 'Edit Room Type' : 'Create New Room Type'}
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Room Type Name"
                                    name="name"
                                    placeholder="e.g. Deluxe King"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    multiline
                                    rows={2}
                                    placeholder="Brief description of the room type..."
                                    value={formData.description}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Base Rate"
                                    name="baseRate"
                                    type="number"
                                    InputProps={{ startAdornment: currencySymbol === '₹' ? '₹' : `${currencySymbol} ` }}
                                    value={formData.baseRate}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Extra Bed Rate"
                                    name="extraBedCharge"
                                    type="number"
                                    InputProps={{ startAdornment: currencySymbol === '₹' ? '₹' : `${currencySymbol} ` }}
                                    value={formData.extraBedCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Max Adults"
                                    name="maxAdults"
                                    type="number"
                                    value={formData.maxAdults}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Max Kids"
                                    name="maxKids"
                                    type="number"
                                    value={formData.maxKids}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    options={amenitiesList.map(a => a.name)}
                                    value={formData.defaultAmenities || []}
                                    onChange={handleAmenitiesChange}
                                    loading={isLoadingSetup}
                                    disabled={isLoadingSetup}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Default Amenities" placeholder="Add amenities..." />
                                    )}
                                />
                            </Grid>

                            {/* Images Upload Section */}
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PhotoLibraryIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle2" fontWeight="700">
                                            Room Type Images ({formData.images?.length || 0})
                                        </Typography>
                                    </Box>
                                </Divider>
                            </Grid>

                            <Grid item xs={12}>
                                <FileUploadDropzone 
                                    accept="image/png,image/jpeg,image/webp,image/jpg" 
                                    maxSizeMB={5}
                                    multiple={true}
                                    onMultipleUploadSuccess={handleImagesUploaded} 
                                />
                            </Grid>

                            {formData.images && formData.images.length > 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, mt: 0.5 }}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                            Uploaded Photos ({formData.images.length})
                                        </Typography>
                                        <Button 
                                            size="small" 
                                            color="error" 
                                            onClick={handleClearAllImages}
                                            disabled={loading}
                                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                        >
                                            Clear All Photos
                                        </Button>
                                    </Box>

                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                        gap: 1.5
                                    }}>
                                        {formData.images.map((imgUrl, idx) => (
                                            <Card
                                                key={idx}
                                                elevation={0}
                                                sx={{
                                                    position: 'relative',
                                                    borderRadius: 2,
                                                    border: idx === 0 ? '2px solid #4f46e5' : '1px solid #e2e8f0',
                                                    height: 95,
                                                    overflow: 'hidden',
                                                    '&:hover .image-action-overlay': {
                                                        opacity: 1
                                                    }
                                                }}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    image={imgUrl}
                                                    alt={`Room type photo ${idx + 1}`}
                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                                
                                                {/* Cover Badge */}
                                                {idx === 0 && (
                                                    <Chip
                                                        icon={<StarIcon sx={{ fontSize: '12px !important', color: '#F59E0B !important' }} />}
                                                        label="Cover"
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 4,
                                                            left: 4,
                                                            height: 20,
                                                            fontSize: '0.65rem',
                                                            bgcolor: 'rgba(0,0,0,0.75)',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            '& .MuiChip-label': { px: 0.5 }
                                                        }}
                                                    />
                                                )}

                                                {/* Action Buttons */}
                                                <Box 
                                                    className="image-action-overlay"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        bgcolor: 'rgba(0, 0, 0, 0.4)',
                                                        borderRadius: 1.5,
                                                        p: 0.25
                                                    }}
                                                >
                                                    {idx !== 0 && (
                                                        <Tooltip title="Set as Cover">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleSetCoverImage(idx)}
                                                                disabled={loading}
                                                                sx={{
                                                                    color: 'white',
                                                                    p: 0.4,
                                                                    '&:hover': { color: '#F59E0B', bgcolor: 'rgba(0,0,0,0.5)' }
                                                                }}
                                                            >
                                                                <StarBorderIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Remove photo">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveImage(idx)}
                                                            disabled={loading}
                                                            sx={{
                                                                color: 'white',
                                                                p: 0.4,
                                                                '&:hover': { color: '#ef4444', bgcolor: 'rgba(0,0,0,0.5)' }
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Card>
                                        ))}
                                    </Box>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        name="status"
                                        value={formData.status}
                                        label="Status"
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="Active">Active</MenuItem>
                                        <MenuItem value="Inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                            <Button fullWidth variant="outlined" onClick={onClose} disabled={loading}>Cancel</Button>
                            <Button 
                                fullWidth 
                                type="submit"
                                variant="contained" 
                                disabled={loading}
                                startIcon={loading && <CircularProgress size={20} color="inherit" />}
                            >
                                {loading ? 'Saving...' : roomType ? 'Update Room Type' : 'Create Room Type'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>

            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification({ ...notification, open: false })}
            >
                <Alert severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
}
